/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-01-20 15:08:51
 *
 *******************************************************/

import _ from 'lodash';
import moment from 'moment';
import React, { Component, Fragment } from 'react';
import { Icon, TextStyle, Button } from '@shopify/polaris';
import {
  PinMajor,
  ArrowUpMinor,
  ArrowDownMinor,
  HideMinor,
  EditMinor,
  DeleteMinor,
  ViewMinor
} from '@shopify/polaris-icons';
import ReactTooltip from 'react-tooltip';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { PRODUCT_ATTRIBUTES, CONDUCTOR_FOR_ATTRIBUTES } from 'src/constants/merchandising-v2';
import { DATE_FORMAT } from 'src/constants/language-setting';
import { getShop, optimizeImage } from 'src/helpers';
import noProductImage from 'src/assets/images/product/no-product-image.png';
import { FilterIconDisable } from 'src/components/containers/common/icon/filterIcon';
import { RankingIconDisable } from 'src/components/containers/common/icon/rankingIcon';
import { formatMoney } from 'src/helpers/money-format';

type Props = {
  consequences: Object,
  ruleId: string,
  handleEditPin: Function,
  handleEditAttribute: Function,
  handleDelete: Function,
  index: number
};

type ComposedProps = Props & WithI18nProps;

export const RenderProductTooltip = (props: Object) => {
  const id = _.uniqueId('tooltip');
  const { item, moneyFormat } = props;
  let image = _.isEmpty(_.get(item, 'images', {})) ? '' : item.images[Object.keys(item.images)[0]];
  image = _.isEmpty(image) ? noProductImage : optimizeImage(image, '160x232');
  const shop = getShop();

  // Get productId (If product is variant, id is variant id, not correct product id)
  const productId = _.get(item, 'product_id', 0)
    ? _.get(item, 'product_id', 0)
    : _.get(item, 'id', 0);
  const label = _.get(item, 'label', '');
  const url = `https://${shop}/admin/products/${productId}`;
  return (
    <Fragment>
      {/* Content */}
      <span data-tip="dummy data" data-for={id}>
        <TextStyle variation="strong">{label}</TextStyle>
      </span>

      {/* Tooltip content */}
      <ReactTooltip
        id={id}
        className="extraTooltipClass Polaris-Tooltip-TooltipOverlay Polaris-Tooltip-TooltipOverlay--positionedAbove"
        type="light"
        delayHide={300}
        effect="solid"
        getContent={function () {
          return (
            <div className="Polaris-Tooltip-TooltipOverlay__Content Polaris-Product-Tooltip-TooltipOverlay__Content">
              <div className="product-image">
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <span>
                    <Icon source={ViewMinor} />
                    <span className="view-product">
                      {props.i18n.translate('AdminHTML.MerchandisingV2.Rule.viewProduct')}
                    </span>
                  </span>
                </a>
                <img style={{ width: '100%' }} src={image} alt={label} />
              </div>
              <label>{label} </label>
              <label style={{ color: '#D72C0D' }}>
                {formatMoney(_.get(item, 'price', 0), moneyFormat)}
              </label>
            </div>
          );
        }}
        place="top"
      />
    </Fragment>
  );
};
class StrategyDetail extends Component<ComposedProps> {
  renderAttributesDisplay = attributes => {
    const { optionsList, i18n, appLanguage, moneyFormat } = this.props;

    const result = [];
    _.map(attributes, item => {
      const { attribute, conductor, value } = item;
      // Attribute display
      // only display for attribute not empty

      if (
        !_.isEmpty(attribute) &&
        !_.isEmpty(conductor) &&
        // In case value is date => isEmpty = true, so need more condition
        (!_.isEmpty(value) ||
          (typeof value === 'object' && ['created_at', 'published_at'].includes(attribute)))
      ) {
        const attributeObject = _.find(
          PRODUCT_ATTRIBUTES,
          productAttribute => productAttribute.value === attribute
        );
        const attributeLabel = _.isEmpty(attributeObject)
          ? _.upperFirst(attribute)
          : i18n.translate(attributeObject.label);

        // Conductor display
        const conductorList =
          CONDUCTOR_FOR_ATTRIBUTES[_.includes(attribute, 'opt_') ? 'option' : attribute];

        const conductorObject = _.isEmpty(conductorList)
          ? {}
          : _.find(conductorList, conductorItem => conductorItem.value === conductor);
        const conductorLabel = _.isEmpty(conductorObject)
          ? i18n.translate('AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label')
          : i18n.translate(conductorObject.label);

        // Value display
        let valueLabel = '';
        switch (attribute) {
          case 'title':
          case 'inventory':
          case 'price': {
            valueLabel = <TextStyle variation="strong">{value}</TextStyle>;
            break;
          }
          case 'product_type':
          case 'vendor':
          case 'tag':
          case 'collection': {
            const { label } = value;
            valueLabel = <TextStyle variation="strong">{label}</TextStyle>;
            break;
          }
          case 'created_at':
          case 'published_at': {
            valueLabel = (
              <TextStyle variation="strong">
                {value ? moment(value).format(DATE_FORMAT[appLanguage].number) : ''}
              </TextStyle>
            );

            break;
          }

          case 'product_id': {
            valueLabel = (
              <RenderProductTooltip item={value} i18n={i18n} moneyFormat={moneyFormat} />
            );
            break;
          }
          // option
          default: {
            const { label = '' } = value;
            valueLabel = <TextStyle variation="strong">{label}</TextStyle>;
          }
        }
        let attributeLabelOption;
        if (_.startsWith(attribute, 'opt_')) {
          const option = _.find(optionsList, { name: attribute.replace('opt_', '') });
          attributeLabelOption = _.get(option, 'originalName', attributeLabel);
        }
        const attributeDisplay = i18n.translate(
          'AdminHTML.MerchandisingV2.ListRules.consequence_Attribute',
          {
            nt_attribute: attributeLabelOption ? attributeLabelOption : attributeLabel,
            nt_conductor: conductorLabel,
            nt_value: valueLabel
          }
        );
        result.push(attributeDisplay);
      }
    });
    return result;
  };

  render() {
    const {
      i18n,
      strategy,
      index,
      handleEditPin,
      handleEditAttribute,
      handleEditProductPerformanceRanking,
      handleDelete,
      moneyFormat,
      ruleType
    } = this.props;

    const { type } = strategy;

    const attributeHeading = {
      boost: {
        title: i18n.translate('AdminHTML.MerchandisingV2.Rule.card3_Boost_Products'),
        icon: <Icon source={ArrowUpMinor} color="subdued" />
      },
      demote: {
        title: i18n.translate('AdminHTML.MerchandisingV2.Rule.card3_Demote_Products'),
        icon: <Icon source={ArrowDownMinor} color="subdued" />
      },
      hide: {
        title: i18n.translate('AdminHTML.MerchandisingV2.Rule.card3_Hide_Products'),
        icon: <Icon source={HideMinor} color="subdued" />
      },
      filter: {
        title: i18n.translate('AdminHTML.MerchandisingV2.Rule.card3_Filter_Products'),
        icon: <Icon source={FilterIconDisable} color="subdued" />
      },
      productPerformanceRanking: {
        title: i18n.translate(
          'AdminHTML.MerchandisingV2.Rule.card3_ProductPerformanceRanking_Products'
        ),
        icon: <Icon source={RankingIconDisable} color="subdued" />
      }
    };
    switch (type) {
      case 'pin': {
        const { pinPositions = [], pinMatchRequired } = strategy;
        return (
          <div className="consequence-detail">
            <div className="consequence-detail-action">
              <Button plain icon={EditMinor} onClick={() => handleEditPin(index)}></Button>
              <Button plain icon={DeleteMinor} onClick={() => handleDelete(index)}></Button>
            </div>
            <div className="consequence-detail-title">
              <Icon source={PinMajor} color="subdued" />
              <TextStyle variation="subdued">
                <span style={{ textTransform: 'uppercase' }}>
                  {i18n.translate('AdminHTML.MerchandisingV2.Rule.card3_Pin_Products')}
                </span>
              </TextStyle>
            </div>
            <div className="consequence-detail-content">
              {pinPositions.map((item, itemIndex) => {
                const value = _.get(item, 'id', {});
                if (!(!_.isEmpty(item) && !_.isEmpty(value) && _.get(value, 'id', null))) {
                  return null;
                }
                return (
                  <div key={itemIndex} style={{ marginBottom: '8px' }}>
                    {i18n.translate('AdminHTML.MerchandisingV2.Rule.consequence_Pin', {
                      nt_product: (
                        <RenderProductTooltip item={value} i18n={i18n} moneyFormat={moneyFormat} />
                      ),
                      nt_position: i18n.ordinal(itemIndex + 1)
                    })}
                  </div>
                );
              })}
            </div>
            {pinMatchRequired && ruleType === 'search' && (
              <p>
                <TextStyle variation="subdued">
                  {i18n.translate('AdminHTML.MerchandisingV2.Rule.consequence_PinMatchRequired')}
                </TextStyle>
              </p>
            )}
          </div>
        );
      }
      case 'productPerformanceRanking': {
        const { queries } = strategy;
        return (
          <div className="consequence-detail">
            <div className="consequence-detail-action">
              <Button
                plain
                icon={EditMinor}
                onClick={() => handleEditProductPerformanceRanking(index)}></Button>
            </div>
            <div className="consequence-detail-title">
              {attributeHeading[type].icon}
              <TextStyle variation="subdued">
                <span style={{ textTransform: 'uppercase' }}>{attributeHeading[type].title}</span>
              </TextStyle>
            </div>
            <div className="consequence-detail-content">
              <p key="product-performance-ranking-summary">
                {queries.map((query, index) => (
                  <p>
                    {i18n.translate(
                      'AdminHTML.MerchandisingV2.Rule.card3_ProductPerformanceRanking_Content',
                      {
                        q: <TextStyle variation="strong">{query}</TextStyle>
                      }
                    )}
                  </p>
                ))}
              </p>
            </div>
          </div>
        );
      }
      case 'boost':
      case 'demote':
      case 'hide':
      case 'filter': {
        const { attributes = [] } = strategy;
        const attributesDisplay = this.renderAttributesDisplay(attributes);
        if (attributesDisplay.length === 0) {
          return null;
        }
        return (
          <div className="consequence-detail">
            <div className="consequence-detail-action">
              <Button plain icon={EditMinor} onClick={() => handleEditAttribute(index)}></Button>
              <Button plain icon={DeleteMinor} onClick={() => handleDelete(index)}></Button>
            </div>
            <div className="consequence-detail-title">
              {attributeHeading[type].icon}
              <TextStyle variation="subdued">
                <span style={{ textTransform: 'uppercase' }}>{attributeHeading[type].title}</span>
              </TextStyle>
            </div>
            <div className="consequence-detail-content">
              {_.map(attributesDisplay, (item, itemIndex) => (
                <p key={itemIndex}>
                  {item}
                  {`${itemIndex < attributesDisplay.length - 1 ? ` and` : ''}`}
                </p>
              ))}
            </div>
          </div>
        );
      }
      default:
        return '';
    }
  }
}

export default withI18n()(StrategyDetail);
